import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputText } from "primereact/inputtext"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"
import { MultiSelect } from "primereact/multiselect"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import { gql, useQuery, useMutation } from "@apollo/client"
import ProviderSummaryEntries from "./PPProviderSummaryEntries"
import { useParams, Link, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

function PPRoviderSummary() {
    const [t] = useTranslation()
    const [globalFilter, setGlobalFilter] = useState(null)
    const [selectedShops, setSelectedShops] = useState(null)
    const navigate = useNavigate()

    let { providerId } = useParams()

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 15,
        sortField: "sharedProducts",
        sortOrder: -1,
        filters: null
    })

    let findActiveSearchFilter = (obj) => {
        let results = []
        for (const property in obj) {
            if (obj[property].value !== null && obj[property].field !== "global") {
                results.push({
                    field: property,
                    value: obj[property]?.value,
                    mode: obj[property]?.matchMode
                })
            }
        }
        return results
    }

    let getCurrentSearchFilters = (globalFilter, filters) => {
        let searchFilter = []
        if (globalFilter != null && globalFilter != "") {
            searchFilter.push({
                field: "global",
                mode: "global",
                value: globalFilter
            })
        } else {
            searchFilter = findActiveSearchFilter(filters)
        }

        if (selectedShops != null && selectedShops.length > 0) {
            searchFilter.push({
                field: "shopName",
                mode: "equals",
                value: selectedShops.join(",")
            })
        }

        if (globalFilter === "") {
            searchFilter.length = 0
        }

        return searchFilter
    }

    let variables = {
        searchFilters: getCurrentSearchFilters(globalFilter, lazyParams.filters),
        page: Math.ceil(lazyParams.first / lazyParams.rows) + 1,
        limit: lazyParams.rows,
        sortField: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder,
    }

    const LOAD_QUERY = gql`
        query providerSummary(
            $searchFilters: [DatatableSearchFilterInput]
            $sortField: String
            $sortOrder: Int!
            $limit: Int!
            $page: Int!
        ) {
            providerSummary(
                searchFilters: $searchFilters
                sortField: $sortField
                sortOrder: $sortOrder
                limit: $limit
                page: $page
            ) {
                providerSummaries {
                    shopName
                    providerName
                    providerId
                    sharedProducts
                    sharedProductsPercent
                    cheaperThanMyShop
                    cheaperThanMyShopPercent
                }
                total
                currentPage
            }

            distinctShopNames
        }
    `

    const PROVIDER_SUMMARY_EXPORT_QUERY = gql`
        mutation startProviderSummaryExport(
            $searchFilters: [DatatableSearchFilterInput]
            $sortField: String
            $sortOrder: Int!
            $limit: Int!
        ) {
            startProviderSummaryExport(
                searchFilters: $searchFilters
                sortField: $sortField
                sortOrder: $sortOrder
                limit: $limit
            ) {
                responseCode
                responseMessage
                downloadUrl
            }
        }
    `

    const { loading, data, refetch } = useQuery(LOAD_QUERY, {
        skip: false,
        displayName: "ProviderSummaryQuery",
        variables: variables,
        fetchPolicy: "no-cache"
    })
    const totalRecords = data?.providerSummary?.total ?? 0
    const shops = data?.distinctShopNames ?? []

    const getExportVariables = () => {
        let variablesExport = { ...variables }
        variablesExport.limit = totalRecords
        return variablesExport
    }

    const [triggerProviderSummaryExport] = useMutation(PROVIDER_SUMMARY_EXPORT_QUERY, {
        variables: getExportVariables(),
        fetchPolicy: "no-cache",
        onCompleted: () => {
            toast(t("export.started"))
        },
        onError: (error2) => {
            if (error2.message.toString().length > 0) {
                toast.error(t("export.start_failed"))
            }
        }
    })

    useEffect(() => {
        loadLazyData()
    }, [lazyParams])

    const loadLazyData = () => {
        refetch()
    }

    const onLazyLoad = (event) => {
        setLazyParams(event)
    }

    const onUpdateFilter = (event) => {
        event.first = 0
        setLazyParams(event)
    }

    const shopFilter = (
        <MultiSelect
            value={selectedShops}
            label={t("provider_summary.shops")}
            options={shops}
            onChange={(e) => setSelectedShops(e.value)}
            placeholder={t("provider_summary.choose_shops")}
            display="chip"
            width={"100px"}
            emptyMessage={t("provider_summary.no_data_received")}
        />
    )

    let providerSummaryClass = "nav-link active"
    let providerSummaryEntriesClass = "nav-link"
    let providerSummaryTabOverviewClass = "tab-pane fade active show"
    let providerSummaryEntriesTabClass = "tab-pane fade"

    if (providerId !== undefined && providerId !== null) {
        providerSummaryClass = "nav-link"
        providerSummaryEntriesClass = "nav-link active"
        providerSummaryTabOverviewClass = "tab-pane fade"
        providerSummaryEntriesTabClass = "tab-pane fade active show"
    }

    const providerNameBodyTemplate = (rowData) => {
        return <Link to={`/providerSummary/${rowData.providerId}`}>{rowData.providerName}</Link>
    }

    const clearSummaryEntryLink = () => {
        navigate("/providerSummary")
    }

    return (
        <>
            <style>
            {`
                .p-datatable.p-datatable-striped .p-datatable-tbody > tr.p-row-odd { 
                    background-color: rgb(243, 245, 252); 
                    color: white; 
                    td {
                        color: #495057;
                    }
                }
            `}
            </style>
            <div className="card shadow card-primary card-outline card-outline-tabs mb-3">
                <div className="card-header p-0 border-bottom-0">
                    <ul className="nav nav-tabs" id="custom-tabs-four-tab" role="tablist">
                        <li className="nav-item">
                            <a
                                className={providerSummaryClass}
                                id="custom-tabs-four-provider_summary-tab"
                                data-bs-toggle="pill"
                                href="#custom-tabs-four-provider_summary"
                                role="tab"
                                onClick={() => clearSummaryEntryLink()}
                                aria-controls="custom-tabs-four-provider_summary"
                                aria-selected="true">
                                {t("provider_summary.tab_summary")}
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={providerSummaryEntriesClass}
                                id="custom-tabs-four-provider_summary_entries-tab"
                                data-bs-toggle="pill"
                                href="#custom-tabs-four-provider_summary_entries"
                                role="tab"
                                onClick={() => window.dispatchEvent(new Event("resize"))}
                                aria-controls="custom-tabs-four-provider_summary_entries"
                                aria-selected="true">
                                {t("provider_summary.tab_summary_details")}
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="card-body">
                    <div className="tab-content" id="custom-tabs-four-tabContent">
                        <div
                            className={providerSummaryTabOverviewClass}
                            id="custom-tabs-four-provider_summary"
                            role="tabpanel"
                            aria-labelledby="custom-tabs-four-provider_summary-tab">
                            <div className="datatable-container">
                                <div className="pp-toolbar">
                                    <div className="pp-toolbar__group">
                                        <span>
                                            {t("provider_summary.count_records")}: {data?.providerSummary?.total ?? 0}
                                        </span>
                                        <IconField iconPosition="left">
                                            <InputIcon className="fas fa-search" />
                                            <InputText
                                                className="p-datatable-global-search"
                                                type="search"
                                                onInput={(e) => setGlobalFilter(e.target.value)}
                                                placeholder="Suche"
                                            />
                                        </IconField>
                                    </div>
                                    <div className="pp-toolbar__group">
                                        <button
                                            id="buttonProviderSummaryExport"
                                            className="btn btn-outline-primary"
                                            type="button"
                                            aria-expanded="false"
                                            aria-controls="exportProviderSummary"
                                            data-bs-toggle="modal"
                                            data-bs-target="#createProviderSummaryExportDialog"
                                            disabled={totalRecords == 0}>
                                            <i className="fas fa-file-excel" /> {t("single.export")}
                                        </button>
                                    </div>
                                </div>
                                <DataTable
                                    className="text-sm p-datatable-custom"
                                    value={data?.providerSummary?.providerSummaries}
                                    lazy
                                    paginator
                                    first={lazyParams.first}
                                    rows={lazyParams.rows}
                                    totalRecords={totalRecords}
                                    onPage={onLazyLoad}
                                    onSort={onLazyLoad}
                                    onFilter={onUpdateFilter}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    filters={lazyParams.filters}
                                    filterDelay={500}
                                    globalFilter={globalFilter}
                                    loading={loading}
                                    filterDisplay="row"
                                    stripedRows
                                    globalFilterFields={["provider_name", "shopName"]}
                                    emptyMessage={t("provider_summary.no_data_received")}
                                    rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}>
                                    <Column
                                        field="providerName"
                                        header={t("provider_summary.column_provider_name")}
                                        sortable
                                        filter
                                        filterMatchMode="contains"
                                        body={providerNameBodyTemplate}
                                        filterPlaceholder={t(
                                            "provider_summary.column_provider_name_search_by"
                                        )}></Column>
                                    <Column
                                        field="shopName"
                                        header={t("provider_summary.shops")}
                                        filter
                                        filterElement={shopFilter}
                                        sortable
                                        style={{ width: "200px" }}></Column>
                                    <Column
                                        field="sharedProducts"
                                        header={t("provider_summary.common_products_count")}
                                        sortable></Column>
                                    <Column
                                        field="sharedProductsPercent"
                                        header={t("provider_summary.common_products_percent")}
                                        sortable></Column>
                                    <Column
                                        field="cheaperThanMyShop"
                                        header={t("provider_summary.cheapter_than_my_products")}
                                        sortable></Column>
                                    <Column
                                        field="cheaperThanMyShopPercent"
                                        header={t("provider_summary.cheapter_than_my_products_in_percent")}
                                        sortable></Column>
                                </DataTable>
                            </div>
                        </div>

                        <div
                            className={providerSummaryEntriesTabClass}
                            id="custom-tabs-four-provider_summary_entries"
                            role="tabpanel"
                            aria-labelledby="custom-tabs-four-provider_summary_entries-tab">
                            <ProviderSummaryEntries />
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="modal fade"
                id="createProviderSummaryExportDialog"
                tabIndex="-1"
                role="dialog"
                aria-labelledby="createProviderSummaryExportDialogLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="createProviderSummaryExportDialogLabel">
                                Export
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            {t("pricetable.export_dialog.question", { countProducts: totalRecords })}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                {t("pricetable.export_dialog.cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={() => {
                                    triggerProviderSummaryExport()
                                }}>
                                {t("pricetable.export_dialog.create")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default PPRoviderSummary
